/**
 * Format Utilities
 * Helper functions for formatting dates, numbers, file sizes, etc.
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Format date to readable string
 * 
 * @param date - Date string or Date object
 * @param formatString - Format pattern (default: 'MMM dd, yyyy HH:mm')
 * @returns Formatted date string
 * 
 * @example
 * formatDate('2025-11-06T10:30:00') // 'Nov 06, 2025 10:30'
 */
export const formatDate = (
  date: string | Date,
  formatString: string = 'MMM dd, yyyy HH:mm'
): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatString);
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Format date to relative time (e.g., "2 hours ago")
 * 
 * @param date - Date string or Date object
 * @returns Relative time string
 * 
 * @example
 * formatRelativeTime('2025-11-06T08:30:00') // '2 hours ago'
 */
export const formatRelativeTime = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
  } catch (error) {
    return 'Unknown';
  }
};

/**
 * Format duration in seconds to readable string
 * 
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 * 
 * @example
 * formatDuration(125) // '2m 5s'
 * formatDuration(3665) // '1h 1m 5s'
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}h ${minutes}m ${secs}s`;
  }
};

/**
 * Format number with thousand separators
 * 
 * @param num - Number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string
 * 
 * @example
 * formatNumber(1234567) // '1,234,567'
 * formatNumber(1234.567, 2) // '1,234.57'
 */
export const formatNumber = (num: number, decimals: number = 0): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format percentage
 * 
 * @param value - Value to format as percentage
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 * 
 * @example
 * formatPercentage(0.756) // '75.6%'
 * formatPercentage(0.5, 0) // '50%'
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Format file size to human-readable string
 * 
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 * 
 * @example
 * formatFileSize(1024) // '1.00 KB'
 * formatFileSize(1048576) // '1.00 MB'
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format currency
 * 
 * @param amount - Amount to format
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted currency string
 * 
 * @example
 * formatCurrency(1234.56) // '$1,234.56'
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Truncate string with ellipsis
 * 
 * @param str - String to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated string
 * 
 * @example
 * truncateString('This is a very long string', 10) // 'This is a...'
 */
export const truncateString = (str: string, maxLength: number = 50): string => {
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength)}...`;
};

/**
 * Capitalize first letter of string
 * 
 * @param str - String to capitalize
 * @returns Capitalized string
 * 
 * @example
 * capitalize('hello world') // 'Hello world'
 */
export const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Convert snake_case to Title Case
 * 
 * @param str - Snake case string
 * @returns Title case string
 * 
 * @example
 * snakeToTitle('people_counting') // 'People Counting'
 */
export const snakeToTitle = (str: string): string => {
  return str
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
};

/**
 * Format job type for display
 * 
 * @param jobType - Job type string
 * @returns Formatted job type string
 * 
 * @example
 * formatJobType('people_counting') // 'People Counting'
 */
export const formatJobType = (jobType: string): string => {
  return snakeToTitle(jobType);
};

/**
 * Format job number with # prefix
 * 
 * @param jobNumber - Job number
 * @returns Formatted job number string
 * 
 * @example
 * formatJobNumber(92) // '#92'
 */
export const formatJobNumber = (jobNumber: number): string => {
  return `#${jobNumber}`;
};